<template>
    <div class="bulk-general-product-information">
        <BAlert
            show
            variant="warning"
            class="app-alert--attention"
        >
            <div class="alert-body d-flex align-items-center">
                <feather-icon
                    icon="InfoIcon"
                    class="mr-1 flex-shrink-0"
                />
                <span>
                    {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):bulk-update-notice') }}
                </span>
            </div>
        </BAlert>

        <HamletStaticTable
            :columns="columns"
            :items="products"
            table-container-class="table-responsive hamlet-table bulk-products-table"
        >
            <template #header>
                <div class="card-header">
                    <h4 class="mb-0">
                        {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):included-products') }}
                    </h4>
                </div>
            </template>

            <template #cell-name="{ item }">
                <div class="d-flex align-items-center">
                    <span>{{ item.name || '—' }}</span>
                    <BBadge
                        v-if="item.isPrimary"
                        variant="light-primary"
                        class="ml-1"
                    >
                        {{ $t('Page(ReceivedRequest):Component(GeneralProductInformation):primary-request') }}
                    </BBadge>
                </div>
            </template>

            <template #cell-identifier="{ item }">
                {{ item.identifier || '—' }}
            </template>

            <template #cell-actions="{ item }">
                <BButton
                    v-if="!item.isPrimary"
                    size="sm"
                    variant="outline-danger"
                    :disabled="runtimeState.isBusy || runtimeState.isSaving"
                    @click="removeRequest(item.requestId)"
                >
                    {{ $t('Component(Button):remove') }}
                </BButton>
            </template>
        </HamletStaticTable>
    </div>
</template>

<script>
import { BAlert, BBadge, BButton } from 'bootstrap-vue'
import HamletStaticTable from '@core/components/common/HamletStaticTableV1.vue'
import {
    getNameOrId,
    ProductIdentifiers,
    ProductNames,
} from '@/libs/product'
import { createGeneralProductInformationV2Validation } from './general-product-information-v2-validation'

export default {
    name: 'BulkGeneralProductInformationStep',
    components: {
        BAlert,
        BBadge,
        BButton,
        HamletStaticTable,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
    },
    data() {
        return {
            columns: [
                {
                    key: 'name',
                    label: this.$t('Component(Label):product-name'),
                },
                {
                    key: 'identifier',
                    label: this.$t('Component(Label):product-id'),
                },
                {
                    key: 'actions',
                    label: '',
                    thClass: 'bulk-products-actions',
                    tdClass: 'bulk-products-actions',
                },
            ],
        }
    },
    computed: {
        products() {
            const selection = this.context.bulkReplyCandidates || {}
            const candidates = Array.isArray(selection.candidates) ? selection.candidates : []
            const selectedRequestIds = Array.isArray(selection.selectedRequestIds)
                ? selection.selectedRequestIds
                : []

            return [
                this.primaryProduct,
                ...selectedRequestIds
                    .map(requestId => candidates.find(candidate => candidate.requestId === requestId))
                    .filter(Boolean)
                    .map(candidate => ({
                        id: `request-${candidate.requestId}`,
                        requestId: candidate.requestId,
                        productId: candidate.product && candidate.product.id,
                        name: candidate.product && candidate.product.name,
                        identifier: candidate.product && candidate.product.identifier,
                        isPrimary: false,
                    })),
            ]
        },
        primaryProduct() {
            const product = this.context.product || {}
            const customerProduct = (this.context.request && this.context.request.customerProduct) || {}

            return {
                id: `request-${this.context.requestId}`,
                requestId: this.context.requestId,
                productId: this.context.productId,
                name: customerProduct.name
                    || getNameOrId(product.names || [], ProductNames.OUR_PRODUCT_NAME)
                    || product.title
                    || product.name
                    || '',
                identifier: customerProduct.identifier
                    || getNameOrId(
                        product.identifiers || [],
                        ProductIdentifiers.PRODUCT_ID,
                    )
                    || '',
                isPrimary: true,
            }
        },
    },
    mounted() {
        this.emitValidity()
    },
    watch: {
        'context.bulkReplyCandidates.selectedRequestIds': {
            deep: true,
            handler: 'emitValidity',
        },
        'runtimeState.isLoading': function onLoadingChange(isLoading) {
            if (!isLoading) {
                this.$nextTick(this.emitValidity)
            }
        },
    },
    methods: {
        removeRequest(requestId) {
            this.$emit('action', {
                type: 'bulk-request.remove',
                payload: { requestId },
            })
        },
        emitValidity() {
            this.$emit(
                'validity-change',
                createGeneralProductInformationV2Validation(this.context, this.settings),
            )
        },
    },
}
</script>

<style lang="scss">
.bulk-products-table table th,
.bulk-products-table table td {
    padding: 0.72rem 1rem;
    vertical-align: middle;
}

.bulk-products-actions {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}
</style>
